import { useCallback, useEffect, useRef } from 'react';
import Phaser from 'phaser';

import { createGameConfig } from '@/features/laneChooser/game/config';
import { createLaneChooserEventBus, EVENTS } from '@/features/laneChooser/game/EventBus';
import { syncPhaserCanvasSize } from '@/shared/utils/phaserCanvasSize';
import { createPhaserGameLifecycle } from '@/shared/utils/phaserGameLifecycle';

import type { RefObject } from 'react';
import type { LaneChooserEventBus } from '@/features/laneChooser/game/EventBus';
import type {
	LaneChooserAnswerRequest,
	LaneChooserAnswerResolution,
	LaneChooserGameTheme,
	LaneChooserQuestionPayload,
	LaneChooserSoundEffect,
} from '@/features/laneChooser/game/types';

type TooltipPoint = { x: number; y: number };

type UseLaneChooserPhaserGameParams = {
	containerRef: RefObject<HTMLDivElement | null>;
	themeConfig: LaneChooserGameTheme;
	laneLinesCount: number;
	roundKey?: number | string;
	question: LaneChooserQuestionPayload | null;
	gameStarted: boolean;
	answerResolution: LaneChooserAnswerResolution | null;
	onAnswerRequest?: (selectedIndex: number) => void;
	onRoundExitComplete?: () => void;
	onSoundEffect?: (effect: LaneChooserSoundEffect) => void;
	onInstructionComplete?: () => void;
	onInstructionTooltipChange?: (textKey: string | null) => void;
	onInstructionTooltipPositionChange?: (point: TooltipPoint | null) => void;
	onQuestionImagePreview?: (imageSrc: string) => void;
};

type UseLaneChooserPhaserGameResult = {
	skipInstructions: () => void;
};

const gameLifecycle = createPhaserGameLifecycle();

export function useLaneChooserPhaserGame(params: UseLaneChooserPhaserGameParams): UseLaneChooserPhaserGameResult {
	const gameRef = useRef<Phaser.Game | null>(null);
	const eventBusRef = useRef<LaneChooserEventBus | null>(null);
	const sceneReadyRef = useRef(false);
	const worldLoadedRef = useRef(false);
	const latestParamsRef = useRef(params);

	latestParamsRef.current = params;

	useEffect(() => {
		let isMounted = true;
		let resizeObserver: ResizeObserver | null = null;
		let removeWindowResizeListener: (() => void) | null = null;

		const mountGame = async () => {
			await gameLifecycle.waitForPendingDestroy();

			const container = latestParamsRef.current.containerRef.current;
			if (!isMounted || !container || gameRef.current) {
				return;
			}

			const eventBus = createLaneChooserEventBus();
			eventBusRef.current = eventBus;

			const emitWorld = () => {
				if (!sceneReadyRef.current || !container.isConnected) {
					return;
				}

				const { laneLinesCount, question, gameStarted, answerResolution } = latestParamsRef.current;
				eventBus.emit(EVENTS.LOAD_WORLD, {
					laneLinesCount,
					viewportWidth: Math.max(1, Math.round(container.clientWidth || window.innerWidth || 1)),
					viewportHeight: Math.max(1, Math.round(container.clientHeight || window.innerHeight || 1)),
					question,
				});
				worldLoadedRef.current = true;
				eventBus.emit(EVENTS.SET_GAME_STARTED, gameStarted);
				if (answerResolution) {
					eventBus.emit(EVENTS.ANSWER_RESOLVED, answerResolution);
				}
			};

			const handleSceneReady = () => {
				if (!gameRef.current) {
					return;
				}

				sceneReadyRef.current = true;
				emitWorld();
			};
			const handleAnswerRequested = ({ selectedIndex }: LaneChooserAnswerRequest) => {
				latestParamsRef.current.onAnswerRequest?.(selectedIndex);
			};
			const handleRoundExitComplete = () => latestParamsRef.current.onRoundExitComplete?.();
			const handleSoundEffect = (effect: LaneChooserSoundEffect) =>
				latestParamsRef.current.onSoundEffect?.(effect);
			const handleInstructionComplete = () => latestParamsRef.current.onInstructionComplete?.();
			const handleInstructionTooltip = (textKey: string | null) => {
				latestParamsRef.current.onInstructionTooltipChange?.(textKey);
			};
			const handleInstructionTooltipPosition = (point: TooltipPoint | null) => {
				latestParamsRef.current.onInstructionTooltipPositionChange?.(point);
			};
			const handleQuestionImagePreview = (imageSrc: string) => {
				latestParamsRef.current.onQuestionImagePreview?.(imageSrc);
			};

			eventBus.on(EVENTS.SCENE_READY, handleSceneReady);
			eventBus.on(EVENTS.ANSWER_REQUESTED, handleAnswerRequested);
			eventBus.on(EVENTS.ROUND_EXIT_COMPLETE, handleRoundExitComplete);
			eventBus.on(EVENTS.SOUND_EFFECT, handleSoundEffect);
			eventBus.on(EVENTS.INSTRUCTION_COMPLETE, handleInstructionComplete);
			eventBus.on(EVENTS.INSTRUCTION_TOOLTIP, handleInstructionTooltip);
			eventBus.on(EVENTS.INSTRUCTION_TOOLTIP_POS, handleInstructionTooltipPosition);
			eventBus.on(EVENTS.QUESTION_IMAGE_PREVIEW, handleQuestionImagePreview);

			const game = new Phaser.Game(createGameConfig(container, eventBus, latestParamsRef.current.themeConfig));
			gameRef.current = game;
			syncPhaserCanvasSize(game, container, { fallback: 'viewport' });

			const handleResize = () => {
				if (!gameRef.current || !container.isConnected) {
					return;
				}

				syncPhaserCanvasSize(gameRef.current, container, { fallback: 'viewport' });
				emitWorld();
			};

			if (typeof ResizeObserver === 'undefined') {
				window.addEventListener('resize', handleResize);
				removeWindowResizeListener = () => window.removeEventListener('resize', handleResize);
			} else {
				resizeObserver = new ResizeObserver(handleResize);
				resizeObserver.observe(container);
			}
		};

		mountGame();

		return () => {
			isMounted = false;
			resizeObserver?.disconnect();
			removeWindowResizeListener?.();

			const game = gameRef.current;
			const eventBus = eventBusRef.current;
			if (game && eventBus) {
				eventBus.removeAllListeners();
				gameLifecycle.destroy(game);
			} else {
				eventBus?.removeAllListeners();
			}

			gameRef.current = null;
			eventBusRef.current = null;
			sceneReadyRef.current = false;
			worldLoadedRef.current = false;
		};
	}, [params.containerRef]);

	useEffect(() => {
		if (!sceneReadyRef.current || !worldLoadedRef.current) {
			return;
		}

		eventBusRef.current?.emit(EVENTS.UPDATE_ROUND, {
			laneLinesCount: params.laneLinesCount,
			question: params.question,
		});
	}, [params.laneLinesCount, params.question, params.roundKey]);

	useEffect(() => {
		if (!sceneReadyRef.current || !worldLoadedRef.current || !params.answerResolution) {
			return;
		}

		eventBusRef.current?.emit(EVENTS.ANSWER_RESOLVED, params.answerResolution);
	}, [params.answerResolution]);

	useEffect(() => {
		if (!sceneReadyRef.current || !worldLoadedRef.current) {
			return;
		}

		eventBusRef.current?.emit(EVENTS.SET_GAME_STARTED, params.gameStarted);
	}, [params.gameStarted]);

	const skipInstructions = useCallback(() => {
		eventBusRef.current?.emit(EVENTS.SKIP_INSTRUCTIONS);
	}, []);

	return { skipInstructions };
}
